import React, { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../lib/GlobalVar';
import { ConsultaRotinas, SalvaRotina, ExecutaRotina, RemoveRotina, ConsultaTemplatesNotif } from '../lib/api';
import Swal from 'sweetalert2';
import Icon from './Icon';
import TemplateEditor from './TemplateEditor';

const TABS = [
    { id: 'todas',    label: 'Todas',     match: () => true },
    { id: 'ativas',   label: 'Ativas',    match: r => r.ativo !== false && (r.status === 'ok' || !r.status) },
    { id: 'pausadas', label: 'Pausadas',  match: r => r.ativo === false },
    { id: 'falha',    label: 'Com falha', match: r => r.status === 'err' }
];

const PASSOS = ['Identidade', 'Comando', 'Frequência', 'Notificação'];

const VAZIO = {
    id: null, nome: '', descricao: '', ativo: true,
    tipo: 'http',
    metodo: 'POST', rota: '/ConsultaInternas', body: '{}',
    script: '', script_cwd: '', timeout_seg: 600,
    cron: '0 8 * * *',
    telegram: { chatId: '', gatilho: 'sempre', template: '' },
    email: { to: '', gatilho: 'erro', assunto: '', template: '' },
    envio: { slug: '', rota: '', formato: 'auto', chatIds: '', emails: '' }
};

const VARS_NOTIF = [
    { token: '{routine}', label: 'nome', desc: 'Nome da rotina', opcoes: async () => { const r = await ConsultaRotinas({}); return (Array.isArray(r) ? r : (r.rotinas || [])).map(x => x.nome).filter(Boolean); } },
    { token: '{status}', label: 'status', desc: 'Resultado', opcoes: ['SUCESSO', 'ERRO'] },
    { token: '{duration}', label: 'duração', desc: 'Tempo de execução (ms)' },
    { token: '{summary}', label: 'resumo/erro', desc: 'Resumo ou mensagem de erro' },
];

export default function Crontab() {
    const { user } = useContext(GlobalContext);
    const [tab, setTab] = useState('todas');
    const [rotinas, setRotinas] = useState([]);
    const [modal, setModal] = useState(null);
    const [outputModal, setOutputModal] = useState(null);

    useEffect(() => { reload(); }, []);

    async function reload() {
        const resp = await ConsultaRotinas({});
        if (resp.Error) return setRotinas([]);
        setRotinas(Array.isArray(resp) ? resp : (resp.rotinas || []));
    }

    async function executar(r) {
        const conf = await Swal.fire({ icon: 'question', title: `Executar "${r.nome}"?`, showCancelButton: true });
        if (!conf.isConfirmed) return;
        const resp = await ExecutaRotina({ id: r.id, solicitante: user?.email });
        if (resp.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: resp.Error });
        setOutputModal({ rotina: r, result: resp });
    }

    async function remover(r) {
        const c = await Swal.fire({ icon: 'question', title: `Remover "${r.nome}"?`, showCancelButton: true });
        if (!c.isConfirmed) return;
        const resp = await RemoveRotina({ id: r.id, solicitante: user?.email });
        if (resp.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: resp.Error });
        reload();
    }

    const counts = TABS.reduce((acc, t) => ({ ...acc, [t.id]: rotinas.filter(t.match).length }), {});
    const visiveis = rotinas.filter(TABS.find(t => t.id === tab).match);

    return (
        <div className="page">
            <div className="page-head">
                <div>
                    <div className="eyebrow"><span className="num">02</span> Crontab Visual</div>
                    <h1 className="page-title">Crontab Visual</h1>
                    <p className="page-sub">Rotinas agendadas via node-cron. Cada rotina pode ser uma chamada HTTP interna ou um script shell na máquina. Notificação Telegram opcional.</p>
                </div>
                <button className="btn" onClick={() => setModal({ ...VAZIO })}>
                    <Icon name="plus" size={12}/> Nova rotina
                </button>
            </div>

            <div className="tabs">
                {TABS.map(t => (
                    <button key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
                        {t.label.toUpperCase()} <span className="tab-count">{String(counts[t.id]).padStart(2, '0')}</span>
                    </button>
                ))}
            </div>

            {visiveis.length === 0 && (
                <div className="empty">Nenhuma rotina nesta aba. Clique em <strong>Nova rotina</strong>.</div>
            )}

            <div className="routine-list">
                {visiveis.map(r => (
                    <div key={r.id} className="routine-card">
                        <div className="routine-head">
                            <span className="id mono">#{String(r.id).padStart(3, '0')}</span>
                            <span className="name">{r.nome}</span>
                            <span className="chip">{(r.tipo || 'http').toUpperCase()}</span>
                            <span className={`status-tag ${r.status || (r.ativo === false ? 'paused' : 'ok')}`}>
                                <span className={`dot ${r.status || (r.ativo === false ? 'paused' : 'ok')}`}/>
                                {(r.ativo === false ? 'PAUSADA' : (r.status || 'OK')).toUpperCase()}
                            </span>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button className="btn ghost sm" onClick={() => setModal({ ...VAZIO, ...r, telegram: { ...VAZIO.telegram, ...(r.telegram || {}) } })}><Icon name="edit" size={11}/></button>
                                <button className="btn ghost sm" onClick={() => executar(r)}><Icon name="play" size={11}/> Executar</button>
                                <button className="btn ghost sm" onClick={() => remover(r)}><Icon name="trash" size={11}/></button>
                            </div>
                        </div>
                        <div className="routine-meta">
                            <span className="mono">{r.cron}</span><span>·</span>
                            <span className="mono">{r.tipo === 'script' ? r.script?.slice(0, 60) + '…' : `${r.metodo || 'POST'} ${r.rota || r.url}`}</span><span>·</span>
                            <span>Último: {r.ultimo || '—'}</span>
                        </div>
                    </div>
                ))}
            </div>

            {modal && <RotinaModal form={modal} setForm={setModal} me={user} onClose={() => setModal(null)} onSaved={() => { setModal(null); reload(); }}/>}
            {outputModal && <OutputModal data={outputModal} onClose={() => setOutputModal(null)}/>}
        </div>
    );
}

function RotinaModal({ form, setForm, me, onClose, onSaved }) {
    const [passo, setPasso] = useState(0);
    const [tplDisp, setTplDisp] = useState([]);
    useEffect(() => { ConsultaTemplatesNotif({}).then(r => setTplDisp(Array.isArray(r) ? r : [])); }, []);

    function up(k, v) { setForm({ ...form, [k]: v }); }
    function upTg(k, v) { setForm({ ...form, telegram: { ...form.telegram, [k]: v } }); }
    function upEm(k, v) { setForm({ ...form, email: { ...(form.email || {}), [k]: v } }); }
    function upEnv(k, v) { setForm({ ...form, envio: { ...(form.envio || {}), [k]: v } }); }
    const csv = (x) => String(x || '').split(',').map(v => v.trim()).filter(Boolean);

    async function salvar() {
        if (!form.nome) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Nome obrigatório.' });
        if (form.tipo === 'http' && !form.rota) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Rota/URL obrigatória.' });
        if (form.tipo === 'script' && !form.script) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Comando do script obrigatório.' });
        if (form.tipo === 'envio') {
            const e = form.envio || {};
            if (!e.slug && !e.rota && !((e.templates) || []).length) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Informe a tela/rota OU selecione ao menos um template.' });
            if (!csv(e.chatIds).length && !csv(e.emails).length) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Informe ao menos um Chat ID ou e-mail de destino.' });
        }
        if (!form.cron) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Expressão cron obrigatória.' });
        const rotinaEnviar = form.tipo === 'envio'
            ? { ...form, envio: { slug: (form.envio.slug || '').trim(), rota: (form.envio.rota || '').trim(), formato: form.envio.formato || 'auto', chatIds: csv(form.envio.chatIds), emails: csv(form.envio.emails), templates: form.envio.templates || [], user_id: me?.id, titulo: form.nome } }
            : form;
        const resp = await SalvaRotina({ solicitante: me?.email, rotina: rotinaEnviar });
        if (resp.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: resp.Error });
        Swal.fire({ icon: 'success', title: 'Rotina salva', timer: 1100, showConfirmButton: false });
        onSaved();
    }

    return (
        <div className="modal-mask" onClick={onClose}>
            <div className="modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
                <div className="modal-head">
                    <div className="eyebrow">{form.id ? `Editar rotina · ${form.nome}` : 'Nova rotina'}</div>
                    <button className="icon-btn" onClick={onClose}><Icon name="x" size={14}/></button>
                </div>
                <div className="modal-body">
                    <div className="tabs" style={{ marginBottom: 14 }}>
                        {PASSOS.map((p, i) => (
                            <button key={p} className={`tab ${passo === i ? 'active' : ''}`} onClick={() => setPasso(i)}>
                                <span className="mono" style={{ marginRight: 4 }}>0{i+1}</span>{p.toUpperCase()}
                            </button>
                        ))}
                    </div>

                    {passo === 0 && (
                        <>
                            <div className="field"><label>Nome</label><input className="input" value={form.nome} onChange={e => up('nome', e.target.value)}/></div>
                            <div className="field"><label>Descrição</label><input className="input" value={form.descricao} onChange={e => up('descricao', e.target.value)}/></div>
                            <label className="row-flex" style={{ fontSize: 12, marginTop: 6 }}>
                                <input type="checkbox" checked={form.ativo !== false} onChange={e => up('ativo', e.target.checked)}/> Ativa
                            </label>
                        </>
                    )}

                    {passo === 1 && (
                        <>
                            <div className="field">
                                <label>Tipo</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, background: 'var(--line)', border: '1px solid var(--line)' }}>
                                    {[
                                        { id: 'http',   lbl: 'HTTP',   sub: 'Chama endpoint (interno ou externo).' },
                                        { id: 'script', lbl: 'Script', sub: 'Executa comando shell na máquina.' },
                                        { id: 'envio',  lbl: 'Envio',  sub: 'Manda print/Excel de uma tela no Telegram/e-mail.' }
                                    ].map(o => (
                                        <button key={o.id} onClick={() => up('tipo', o.id)} style={{ background: form.tipo === o.id ? 'var(--bg-2)' : 'var(--paper)', border: 'none', padding: '12px 14px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', borderLeft: form.tipo === o.id ? '2px solid var(--ink)' : '2px solid transparent' }}>
                                            <div style={{ fontSize: 13, fontWeight: form.tipo === o.id ? 500 : 400 }}>{o.lbl}</div>
                                            <div style={{ fontSize: 10.5, color: 'var(--ink-mute)', marginTop: 2 }}>{o.sub}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {form.tipo === 'http' && (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 10 }}>
                                        <div className="field"><label>Método</label><select className="select" value={form.metodo} onChange={e => up('metodo', e.target.value)}>
                                            {['GET','POST','PUT','DELETE'].map(m => <option key={m}>{m}</option>)}
                                        </select></div>
                                        <div className="field"><label>Rota (interna ou URL)</label><input className="input mono" placeholder="/ConsultaInternas ou http://..." value={form.rota} onChange={e => up('rota', e.target.value)}/></div>
                                    </div>
                                    {form.metodo !== 'GET' && (
                                        <div className="field"><label>Body (JSON)</label><textarea className="textarea mono" rows={5} value={form.body} onChange={e => up('body', e.target.value)}/></div>
                                    )}
                                </>
                            )}

                            {form.tipo === 'script' && (
                                <>
                                    <div className="field">
                                        <label>Comando shell</label>
                                        <textarea className="textarea mono" rows={4} value={form.script} onChange={e => up('script', e.target.value)} placeholder="bash /root/scripts/backup.sh"/>
                                        <span className="muted" style={{ fontSize: 11 }}>Executado no contexto do usuário do backend. stdout/stderr capturados como retorno.</span>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                                        <div className="field"><label>Diretório (opcional)</label><input className="input mono" placeholder="/root" value={form.script_cwd} onChange={e => up('script_cwd', e.target.value)}/></div>
                                        <div className="field"><label>Timeout (seg)</label><input className="input mono" type="number" value={form.timeout_seg} onChange={e => up('timeout_seg', parseInt(e.target.value, 10) || 600)}/></div>
                                    </div>
                                </>
                            )}

                            {form.tipo === 'envio' && (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                        <div className="field"><label>Slug da tela interna</label><input className="input mono" value={(form.envio && form.envio.slug) || ''} onChange={e => upEnv('slug', e.target.value)} placeholder="listacadastrofashion"/></div>
                                        <div className="field"><label>ou Rota direta</label><input className="input mono" value={(form.envio && form.envio.rota) || ''} onChange={e => upEnv('rota', e.target.value)} placeholder="/Dashboard"/></div>
                                    </div>
                                    <div className="field"><label>Formato</label>
                                        <select className="select" value={(form.envio && form.envio.formato) || 'auto'} onChange={e => upEnv('formato', e.target.value)}>
                                            <option value="auto">Automático (gráfico→PNG, tabela→Excel)</option>
                                            <option value="png">Imagem PNG</option>
                                            <option value="pdf">PDF</option>
                                            <option value="xlsx">Excel (só dados)</option>
                                        </select>
                                    </div>
                                    <div className="field"><label>Chat IDs Telegram (vírgula)</label><input className="input mono" value={(form.envio && form.envio.chatIds) || ''} onChange={e => upEnv('chatIds', e.target.value)} placeholder="700119433, -100..."/></div>
                                    <div className="field"><label>E-mails (vírgula)</label><input className="input mono" value={(form.envio && form.envio.emails) || ''} onChange={e => upEnv('emails', e.target.value)} placeholder="alguem@babita.com.br"/></div>
                                    {tplDisp.length > 0 && (
                                        <div className="field">
                                            <label>Combinar templates (opcional)</label>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 150, overflow: 'auto', border: '1px solid var(--line)', padding: 8 }}>
                                                {tplDisp.map(t => (
                                                    <label key={t.id} style={{ display: 'flex', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                                                        <input type="checkbox" checked={((form.envio && form.envio.templates) || []).includes(t.id)} onChange={() => { const cur = (form.envio && form.envio.templates) || []; upEnv('templates', cur.includes(t.id) ? cur.filter(x => x !== t.id) : [...cur, t.id]); }}/>
                                                        {t.nome}
                                                    </label>
                                                ))}
                                            </div>
                                            <span className="muted" style={{ fontSize: 11 }}>Os textos selecionados são resolvidos (variáveis + KPIs) e enviados juntos no horário.</span>
                                        </div>
                                    )}
                                    <span className="muted" style={{ fontSize: 11 }}>No horário do cron, gera o print/Excel da tela (se houver) + templates e envia. O print usa a sua sessão.</span>
                                </>
                            )}
                        </>
                    )}

                    {passo === 2 && (
                        <div className="field">
                            <label>Expressão cron (min hora dia mês dia-semana)</label>
                            <input className="input mono" value={form.cron} onChange={e => up('cron', e.target.value)}/>
                            <span className="muted" style={{ fontSize: 11 }}>Exemplos: <span className="mono">0 8 * * *</span> 8h todo dia · <span className="mono">*/15 * * * *</span> a cada 15min · <span className="mono">0 0 1 * *</span> dia 1 de cada mês.</span>
                        </div>
                    )}

                    {passo === 3 && (
                        <>
                            <div className="field"><label>Chat ID Telegram</label><input className="input mono" value={form.telegram.chatId} onChange={e => upTg('chatId', e.target.value)} placeholder="-100... (deixe vazio pra desligar)"/></div>
                            <div className="field">
                                <label>Gatilho</label>
                                <select className="select" value={form.telegram.gatilho} onChange={e => upTg('gatilho', e.target.value)}>
                                    <option value="sempre">Sempre que executar</option>
                                    <option value="erro">Só em erro</option>
                                    <option value="sucesso">Só em sucesso</option>
                                </select>
                            </div>
                            <div className="field"><label>Template Telegram (opcional)</label><TemplateEditor value={form.telegram.template} onChange={t => upTg('template', t)} rows={3} placeholder="{routine}: {status} em {duration}" vars={VARS_NOTIF}/></div>

                            <div className="eyebrow" style={{ marginTop:14 }}>Notificação por e-mail</div>
                            <div className="field"><label>Destinatário (deixe vazio pra desligar)</label><input className="input mono" value={(form.email && form.email.to) || ''} onChange={e => upEm('to', e.target.value)} placeholder="alguem@babita.com.br"/></div>
                            <div className="field">
                                <label>Gatilho</label>
                                <select className="select" value={(form.email && form.email.gatilho) || 'erro'} onChange={e => upEm('gatilho', e.target.value)}>
                                    <option value="sempre">Sempre que executar</option>
                                    <option value="erro">Só em erro (recomendado)</option>
                                    <option value="sucesso">Só em sucesso</option>
                                </select>
                            </div>
                            <div className="field"><label>Assunto (opcional)</label><input className="input" value={(form.email && form.email.assunto) || ''} onChange={e => upEm('assunto', e.target.value)} placeholder="[Babita] {routine}: {status}"/></div>
                            <div className="field"><label>Corpo do e-mail (opcional)</label><TemplateEditor value={(form.email && form.email.template) || ''} onChange={t => upEm('template', t)} rows={3} placeholder="Rotina {routine} terminou com {status} em {duration}. {summary}" vars={VARS_NOTIF}/></div>
                        </>
                    )}
                </div>
                <div className="modal-foot">
                    <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
                    {passo > 0 && <button className="btn ghost sm" onClick={() => setPasso(p => p - 1)}>Voltar</button>}
                    {passo < 3 && <button className="btn ghost sm" onClick={() => setPasso(p => p + 1)}>Próximo</button>}
                    <button className="btn sm" onClick={salvar}>{form.id ? 'Atualizar' : 'Criar rotina'}</button>
                </div>
            </div>
        </div>
    );
}

function OutputModal({ data, onClose }) {
    const { rotina, result } = data;
    return (
        <div className="modal-mask" onClick={onClose}>
            <div className="modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
                <div className="modal-head">
                    <div className="eyebrow">Resultado · {rotina.nome}</div>
                    <button className="icon-btn" onClick={onClose}><Icon name="x" size={14}/></button>
                </div>
                <div className="modal-body">
                    <div className="row-flex" style={{ marginBottom: 10 }}>
                        <span className={`status-tag ${result.status}`}>{(result.status || '').toUpperCase()}</span>
                        <span className="muted">Duração: {result.duracao}ms</span>
                    </div>
                    {result.erro && <div className="card" style={{ background: 'var(--bg-2)', borderColor: 'var(--status-err)', marginBottom: 10 }}><div className="eyebrow">Erro</div><pre className="mono" style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>{result.erro}</pre></div>}
                    {result.resposta && (
                        <>
                            <div className="eyebrow" style={{ marginBottom: 8 }}>Resposta</div>
                            <pre className="code-block mono" style={{ fontSize: 11, maxHeight: 360 }}>{
                                typeof result.resposta === 'string'
                                    ? result.resposta
                                    : JSON.stringify(result.resposta, null, 2)
                            }</pre>
                        </>
                    )}
                </div>
                <div className="modal-foot">
                    <button className="btn sm" onClick={onClose}>OK</button>
                </div>
            </div>
        </div>
    );
}
